import React, { useEffect } from "react";
import { Box, Button } from "@mui/material";

const ButtonSection = ({
  handleSaveNextDefault,
  handleMarkReviewDefault,
  handleClearResponse,
  handleSubmit,
  currentQuestion,
  handleBack,
  sectionNames,
  subjectNames,
  activeSubjectId,
  activeSectionId,
  setActiveSectionId,
  setActiveSubjectId,
  question,
  questionData,
  handleNext,
}) => {
  const switchSection = () => {
    // Only proceed if we're at the last question
    if (currentQuestion !== questionData?.length - 1) return;

    // Find current subject and section indices
    const subjectCurrentIndex = subjectNames.findIndex(
      (item) => item.subject_id === activeSubjectId
    );

    // Check if we're already at the very last section of the last subject
    const isLastSectionOfLastSubject =
      subjectCurrentIndex === subjectNames.length - 1 &&
      sectionNames[subjectCurrentIndex]?.length - 1 ===
      sectionNames[subjectCurrentIndex]?.findIndex(
        (section) => section.section_id === activeSectionId
      );

    if (isLastSectionOfLastSubject) {
      return;
    }

    // Find current section index within current subject
    let sectionCurrentIndex = -1;
    if (sectionNames[subjectCurrentIndex]) {
      sectionCurrentIndex = sectionNames[subjectCurrentIndex].findIndex(
        (section) => section.section_id === activeSectionId
      );
    }

    // If not found in current subject, search all subjects
    if (sectionCurrentIndex === -1) {
      for (let i = 0; i < sectionNames.length; i++) {
        const foundIndex = sectionNames[i].findIndex(
          (section) => section.section_id === activeSectionId
        );
        if (foundIndex !== -1) {
          sectionCurrentIndex = foundIndex;
          // Update subject index if section was found in different subject
          if (i !== subjectCurrentIndex) {
            subjectCurrentIndex = i;
          }
          break;
        }
      }
    }

    // Determine next section
    if (sectionCurrentIndex !== -1) {
      // Check if there's a next section in current subject
      if (sectionCurrentIndex + 1 < sectionNames[subjectCurrentIndex].length) {
        setActiveSectionId(
          sectionNames[subjectCurrentIndex][sectionCurrentIndex + 1].section_id
        );
      }
      // Otherwise move to first section of next subject
      else if (subjectCurrentIndex + 1 < subjectNames.length) {
        setActiveSubjectId(subjectNames[subjectCurrentIndex + 1].subject_id);
        setActiveSectionId(sectionNames[subjectCurrentIndex + 1][0].section_id);
      }
    } else {
      console.error("Section not found in any subject");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 1, sm: 1.5, md: 1 },
        p: { xs: "8px 10px", sm: "10px 12px", md: "10px 16px" },
        borderTop: "1px solid #ddd",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        position: { xs: "fixed", md: "sticky" },
        bottom: 0,
        left: { xs: 0, md: "unset" },
        right: { xs: 0, md: "unset" },
        zIndex: { xs: 50, md: 5 },
        background: "#fff",
        boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
        flexWrap: "wrap", // Allow main container to wrap its sections
      }}
    >
      {/* Left side buttons */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, md: 2 },
          width: { xs: "100%", md: "auto" },
          justifyContent: { xs: "space-between", md: "flex-start" },
          flexWrap: "nowrap"
        }}
      >
        <Button
          variant="outlined"
          sx={{
            borderColor: "black !important",
            color: "black",
            borderRadius: { xs: "10px", md: "4px" },
            fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
            padding: { xs: "8px 4px", sm: "8px 8px", md: "8px 12px" },
            minWidth: 0,
            lineHeight: 1.1,
            fontWeight: { xs: "bold", md: "500" },
            textTransform: "uppercase",
            flex: { xs: 1, md: "unset" },
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "black", backgroundColor: "rgba(0,0,0,0.04)" }
          }}
          onClick={() => {
            handleMarkReviewDefault("marked");
            switchSection();
          }}
        >
          Mark for Review & Next
        </Button>
        <Button
          variant="outlined"
          sx={{
            borderColor: "black !important",
            color: "black",
            borderRadius: { xs: "10px", md: "4px" },
            fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
            padding: { xs: "8px 4px", sm: "8px 8px", md: "8px 12px" },
            minWidth: 0,
            lineHeight: 1.1,
            fontWeight: { xs: "bold", md: "500" },
            textTransform: "uppercase",
            flex: { xs: 1, md: "unset" },
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "black", backgroundColor: "rgba(0,0,0,0.04)" }
          }}
          onClick={handleClearResponse}
        >
          Clear Response
        </Button>
      </Box>

      {/* Right side buttons */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 1, md: 1.5 },
          width: { xs: "100%", md: "auto" },
          justifyContent: { xs: "space-between", md: "flex-end" },
          mt: { xs: 0.5, md: 0 },
          flexWrap: "wrap"
        }}
      >
        <Box sx={{
          display: "flex",
          gap: { xs: 1, md: 2 },
          flex: { xs: 1, md: "unset" }
        }}>
          <Button
            variant="outlined"
            fullWidth
            disabled={currentQuestion === 0}
            sx={{
              borderColor: "black !important",
              color: "black",
              borderRadius: { xs: "10px", md: "4px" },
              fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
              padding: { xs: "8px 4px", sm: "8px 8px", md: "8px 12px" },
              minWidth: 0,
              lineHeight: 1.1,
              textTransform: "uppercase",
              fontWeight: { xs: "bold", md: "500" },
              "&:hover": { borderColor: "black", backgroundColor: "rgba(0,0,0,0.04)" },
              "&:disabled": {
                borderColor: "#ccc !important",
                color: "#ccc"
              }
            }}
            onClick={() => {
              handleBack();
            }}
          >
            Previous
          </Button>
        </Box>

        <Button
          variant="contained"
          sx={{
            borderRadius: { xs: "10px", md: "4px" },
            backgroundColor: "#337ab7",
            fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
            padding: { xs: "8px 4px", sm: "8px 8px", md: "8px 12px" },
            minWidth: 0,
            whiteSpace: "nowrap",
            lineHeight: 1.1,
            textTransform: "uppercase",
            fontWeight: { xs: "bold", md: "500" },
            boxShadow: "none",
            flex: { xs: 1, md: "unset" },
            "&:hover": { backgroundColor: "#286090", boxShadow: "none" },
          }}
          onClick={() => {
            handleSaveNextDefault();
            switchSection();
          }}
        >
          Save & Next
        </Button>
      </Box>
    </Box>
  );
};

export default ButtonSection;
